import { useCallback, FunctionComponent } from "react";
import { EmotionJSX } from "@emotion/react/types/jsx-namespace";
import {
  Box,
  Radio,
  Paper,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";

import { Color } from "../colors";
import { stylesheet } from "../stylesheet";
import {
  ASSET_SELECTION_TEMPLATE_OPTIONS,
  AssetSelectionTemplate,
} from "./AssetSelectionDialog";

export interface AssetSelectionDataTemplateProps {
  selectedTemplate: AssetSelectionTemplate;
  setSelectedTemplate: (template: AssetSelectionTemplate) => void;
}

export const AssetSelectionDataTemplate: FunctionComponent<
  AssetSelectionDataTemplateProps
> = ({ selectedTemplate, setSelectedTemplate }) => {
  const renderBoxes = useCallback((n: number) => {
    const boxes: EmotionJSX.Element[] = [];
    while (n > 0) {
      boxes.push(<Box key={n} css={ss.box} />);
      n--;
    }

    return boxes;
  }, []);

  const renderOptionLabel = useCallback(
    (option: AssetSelectionTemplate) => {
      switch (option) {
        case "image-grid-with-details":
          return (
            <Box css={[ss.rootBox, ss.higherRootBox]}>
              <Box css={[ss.boxWrapper, ss.gridBox]}>{renderBoxes(8)}</Box>
              <Box css={[ss.boxWrapper, ss.detailsBox]}>{renderBoxes(8)}</Box>
            </Box>
          );
        case "image-grid":
          return (
            <Box css={ss.rootBox}>
              <Box css={[ss.boxWrapper, ss.gridBox]}>{renderBoxes(8)}</Box>
            </Box>
          );
        case "single-image-with-details":
          return (
            <Box css={[ss.rootBox, ss.rowRootBox]}>
              <Box css={[ss.boxWrapper, ss.singleBox]}>{renderBoxes(1)}</Box>
              <Box css={[ss.boxWrapper, ss.detailsBox]}>{renderBoxes(10)}</Box>
            </Box>
          );
        case "single-image":
          return (
            <Box css={[ss.rootBox, ss.rowRootBox, ss.smallerRootBox]}>
              <Box css={[ss.boxWrapper, ss.singleBox]}>{renderBoxes(1)}</Box>
            </Box>
          );
        default:
          throw new Error(`Invalid ${option} option label`);
      }
    },
    [renderBoxes]
  );

  return (
    <Paper variant="outlined" elevation={0} css={ss.wrapper}>
      <FormControl>
        <RadioGroup
          row
          value={selectedTemplate}
          onChange={(e) => {
            setSelectedTemplate(e.target.value as AssetSelectionTemplate);
          }}
        >
          {ASSET_SELECTION_TEMPLATE_OPTIONS.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option}
              control={<Radio />}
              css={ss.radioInput}
              labelPlacement="bottom"
              label={renderOptionLabel(option)}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  );
};

const ss = stylesheet({
  higherRootBox: { height: "200px" },
  smallerRootBox: { width: "135px" },
  box: { backgroundColor: Color.MediumGray },
  singleBox: { gridTemplateColumns: "repeat(1, 1fr)" },
  rowRootBox: { flexDirection: "row", width: "220px" },
  detailsBox: { gridTemplateColumns: "repeat(2, 1fr)" },
  radioInput: (theme) => ({ margin: `0 ${theme.spacing(1)}` }),
  gridBox: {
    gridTemplateColumns: "repeat(4, 1fr)",
    "& div": { minHeight: "20px" },
  },
  wrapper: (theme) => ({
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    margin: `${theme.spacing(2)} 0`,
  }),
  boxWrapper: (theme) => ({
    display: "grid",
    flex: "1 0 auto",
    gap: theme.spacing(1),
  }),
  rootBox: (theme) => ({
    display: "flex",
    flexDirection: "column",
    width: "180px",
    height: "135px",
    boxShadow: `0 0 0 2px ${Color.MediumGray}`,
    padding: `${theme.spacing(6)} ${theme.spacing(1)} ${theme.spacing(1)}`,
    position: "relative",
    gap: theme.spacing(1),
    ":before": {
      content: '"NAME"',
      backgroundColor: Color.MediumGray,
      color: Color.White,
      fontWeight: 600,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      padding: theme.spacing(1),
      fontSize: theme.typography.fontSize,
    },
  }),
});
